import React, { useState, useEffect, lazy, Suspense } from "react";
import Layout from "./Layout.jsx";
import Login from "./Login";
import { isAuthed, getUser } from "@/api/auth";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";

// Pro-Route Code-Splitting: jede Seite wird erst beim Öffnen geladen (statt alle 8 im
// First-Load-Bundle). Öffnet man Morpheus, zieht das Bundle nicht mehr Kreieren/AmBall/
// react-markdown mit. Login bleibt eager (Einstiegsschirm, sofort sichtbar).
// Import-Fns EINMAL definiert → für lazy() UND fürs Idle-Prefetch (unten), damit ein
// Tab-Wechsel instant bleibt statt kurz "lädt…" zu zeigen.
const loaders = {
  Morpheus: () => import("./Morpheus"),
  Kreieren: () => import("./Kreieren"),
  Kobold: () => import("./Kobold"),
  Booker: () => import("./Booker"),
  Bauen: () => import("./Bauen"),
  Verbinden: () => import("./Verbinden"),
  Promotion: () => import("./Promotion"),
};
const Morpheus = lazy(loaders.Morpheus);
const Kreieren = lazy(loaders.Kreieren);
const Kobold = lazy(loaders.Kobold);
const Booker = lazy(loaders.Booker);
const Bauen = lazy(loaders.Bauen);
const Verbinden = lazy(loaders.Verbinden);
const Promotion = lazy(loaders.Promotion);

// Morpheus MUSS erster Key bleiben: _getCurrentPage faellt auf Object.keys(PAGES)[0] zurueck.
const PAGES = {
  Morpheus: Morpheus,
  Kreieren: Kreieren,
  Kobold: Kobold,
  Booker: Booker,
  Verbinden: Verbinden,
  Promotion: Promotion,
  Bauen: Bauen,
};

// Tab-Titel = wo du gerade bist (02.08., fuer mehrere offene Tabs): "MORPHEUS" auf
// der Hauptseite, sonst "MORPHEUS · <AGENT>" — dieselben Labels wie im Nav (ModeHeader).
const TAB_LABELS = { Morpheus: "", Promotion: "HERALD", Kreieren: "KREIEREN",
  Kobold: "KOBOLD", Booker: "BOOKER", Verbinden: "SPIDER", Bauen: "EINSTEIN" };

const RouteFallback = () => (
  <div className="min-h-[70vh] flex items-center justify-center text-ether text-sm">lädt…</div>
);

function _getCurrentPage(url) {
  if (url.endsWith("/")) {
    url = url.slice(0, -1);
  }
  let urlLastPart = url.split("/").pop();
  if (urlLastPart.includes("?")) {
    urlLastPart = urlLastPart.split("?")[0];
  }
  const pageName = Object.keys(PAGES).find((page) => page.toLowerCase() === urlLastPart.toLowerCase());
  return pageName || Object.keys(PAGES)[0];
}

function PagesContent() {
  const location = useLocation();
  const currentPage = _getCurrentPage(location.pathname);

  useEffect(() => {
    const l = TAB_LABELS[currentPage];
    document.title = l ? ('MORPHEUS · ' + l) : 'MORPHEUS';
  }, [currentPage]);

  // Nach dem ersten Paint im Idle die restlichen Seiten-Chunks vorwärmen → Tab-Wechsel
  // instant (Chunk schon im Cache), ohne den First-Load zu belasten. import() ist dedupt.
  useEffect(() => {
    const idle = window.requestIdleCallback || ((f) => setTimeout(f, 200));
    const id = idle(() => { Object.values(loaders).forEach((fn) => { try { fn(); } catch (e) {} }); });
    return () => { (window.cancelIdleCallback || clearTimeout)(id); };
  }, []);

  return (
    <Layout currentPageName={currentPage}>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Morpheus />} />
          <Route path="/Morpheus" element={<Morpheus />} />
          <Route path="/Kreieren" element={<Kreieren />} />
          <Route path="/Kobold" element={<Kobold />} />
          <Route path="/Booker" element={<Booker />} />
          <Route path="/Bauen" element={<Bauen />} />
          <Route path="/Verbinden" element={<Verbinden />} />
          <Route path="/Promotion" element={<Promotion />} />
          {/* Alte Pfade (gemerkte URLs) → neue Personen-Seiten */}
          <Route path="/Chat" element={<Navigate to="/" replace />} />
          <Route path="/Fliegen" element={<Navigate to="/kobold" replace />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

export default function Pages() {
  const [authed, setAuthed] = useState(isAuthed());

  if (!authed) {
    return <Login onSuccess={() => setAuthed(true)} />;
  }
  // Booking-Team (31.10.2026) sieht nur seine Seite.
  if (getUser()?.role === "booking") {
    window.location.replace("/booking");
    return null;
  }

  return (
    <Router>
      <PagesContent />
    </Router>
  );
}
